// src/pages/CouponsPage.js
import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';

// Sentinel value meaning "always visible to mall shoppers, no GPS check"
const ALWAYS_VISIBLE_RADIUS = 99999;

const EMPTY_FORM = {
  storeId: '', title: '', description: '',
  discountType: 'FLAT', discountValue: '', capType: 'FLAT',
  code: '', maxRedemptions: '', validFrom: '', validUntil: '',
  scope: 'store', productIds: [],
};

const DISCOUNT_TYPES = [
  { value: 'FLAT',         label: 'Fixed Amount (₹)' },
  { value: 'PERCENTAGE',   label: 'Percentage (%)' },
  { value: 'INTENT_BASED', label: 'Intent-Based' },
];

const discountValueLabel = (type) =>
  type === 'INTENT_BASED' ? 'Maximum Discount Cap' : 'Discount Value';

const discountPrefix = (type, value) =>
  type === 'FLAT' ? `₹${value}` :
  type === 'PERCENTAGE' ? `${value}%` :
  `up to ₹${value}`;

const couponDiscountDisplay = (coupon) => {
  const amount = coupon.capType === 'PERCENTAGE' ? `${coupon.discountValue}%` : `₹${coupon.discountValue}`;
  if (coupon.discountType === 'INTENT_BASED') {
    return { value: amount, type: 'MAX', detail: `up to ${amount} intent-based` };
  }
  const value = coupon.discountType === 'PERCENTAGE' ? `${coupon.discountValue}%` : `₹${coupon.discountValue}`;
  return {
    value,
    type: coupon.discountType === 'PERCENTAGE' ? 'OFF' : 'FLAT',
    detail: `${value} ${coupon.discountType === 'PERCENTAGE' ? 'off' : 'flat off'}`,
  };
};

const randomCode = () => Math.random().toString(36).substring(2, 8).toUpperCase();
const toLocalDate = (date) => new Date(date - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ width: 15, height: 15 }}>
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
  </svg>
);
const TagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
    <line x1="7" y1="7" x2="7.01" y2="7"/>
  </svg>
);
const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
    <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
  </svg>
);

export default function CouponsPage() {
  const { user } = useAuth();
  const location = useLocation();
  const pendingOpenNew = useRef(!!location.state?.openNew);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [products, setProducts] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showTime, setShowTime] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  useEffect(() => {
    api.getStores().then(data => {
      const owned = data.filter(s => s.ownerId === user?.storeOwner?.id);
      setStores(owned);
      if (owned.length) setSelectedStore(owned[0].id);
    });
  }, [user]);

  useEffect(() => {
    if (!selectedStore) return;
    setLoading(true);
    Promise.all([
      api.getCoupons(selectedStore),
      api.getProducts(selectedStore),
    ]).then(([c, p]) => { setCoupons(c); setProducts(p); }).finally(() => setLoading(false));
    if (pendingOpenNew.current) {
      pendingOpenNew.current = false;
      openNew();
    }
  }, [selectedStore]);

  const openNew = () => {
    const now = new Date();
    const week = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    setForm({ ...EMPTY_FORM, storeId: selectedStore, code: randomCode(), validFrom: toLocalDate(now), validUntil: toLocalDate(week) });
    setError('');
    setShowAdvanced(false);
    setShowTime(false);
    setProductSearch('');
    setShowProductDropdown(false);
    setModal('new');
  };

  const handle = e => setForm({ ...form, [e.target.name]: e.target.value });

  const toggleTime = (on) => {
    setShowTime(on);
    setForm(f => ({
      ...f,
      validFrom: on ? `${f.validFrom}T00:00` : f.validFrom.slice(0, 10),
      validUntil: on ? `${f.validUntil}T23:59` : f.validUntil.slice(0, 10),
    }));
  };

  const save = async e => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const prefix = form.discountType === 'INTENT_BASED'
        ? `up to ${form.capType === 'PERCENTAGE' ? `${form.discountValue}%` : `₹${form.discountValue}`}`
        : discountPrefix(form.discountType, form.discountValue);
      const validFrom = showTime ? form.validFrom : `${form.validFrom}T00:00`;
      const validUntil = showTime ? form.validUntil : `${form.validUntil}T23:59`;
      const isIntent = form.discountType === 'INTENT_BASED';
      const base = {
        storeId: form.storeId, title: '', description: form.description,
        discountType: form.discountType, discountValue: parseFloat(form.discountValue),
        ...(form.discountType === 'INTENT_BASED' ? { capType: form.capType } : {}),
        code: form.code, validFrom, validUntil,
        maxRedemptions: form.maxRedemptions ? parseInt(form.maxRedemptions) : undefined,
        triggerType: isIntent ? 'proximity' : 'always',
        triggerRadiusM: isIntent ? 100 : ALWAYS_VISIBLE_RADIUS,
      };
      if (form.scope === 'products' && form.productIds.length > 0) {
        await Promise.all(form.productIds.map(pid => {
          const product = products.find(p => p.id === pid);
          const title = form.title.trim() || `${prefix} off ${product?.name ?? 'product'}`;
          return api.createCoupon({ ...base, title, productId: pid });
        }));
      } else {
        const title = form.title.trim() || `${prefix} off — Store-wide`;
        await api.createCoupon({ ...base, title, productId: undefined });
      }
      setCoupons(await api.getCoupons(selectedStore));
      setModal(null);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this coupon?')) return;
    await api.deactivateCoupon(id);
    setCoupons(coupons.filter(c => c.id !== id));
  };

  const [viewCoupon, setViewCoupon] = useState(null);

  const isExpired = (c) => new Date(c.validUntil) < new Date();
  const expiresIn = (c) => {
    const h = Math.max(0, Math.floor((new Date(c.validUntil) - new Date()) / 3600000));
    return h < 24 ? `${h}h left` : `${Math.floor(h / 24)}d left`;
  };

  return (
    <div className="dash-root">
      <Sidebar active="coupons" />
      <main className="dash-main">
        <div className="page-content">
          <div className="page-header">
            <div>
              <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.5px' }}>
                Coupons
              </h1>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: 2 }}>
                {coupons.length} active deal{coupons.length !== 1 ? 's' : ''}
              </p>
            </div>
            <button className="btn btn-primary" onClick={openNew} disabled={!selectedStore}>
              <PlusIcon /> New Coupon
            </button>
          </div>

          {/* Store tabs (multi-store) */}
          {stores.length > 1 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
              {stores.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSelectedStore(s.id)}
                  style={{
                    padding: '6px 14px',
                    border: '1px solid',
                    borderColor: selectedStore === s.id ? 'var(--primary)' : 'var(--border)',
                    background: selectedStore === s.id ? 'var(--primary-light)' : 'var(--surface)',
                    color: selectedStore === s.id ? 'var(--primary)' : 'var(--text-secondary)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}><div className="spinner" /></div>
          ) : coupons.length === 0 ? (
            <div className="table-card">
              <div className="empty-state">
                <TagIcon />
                <h3>No active coupons</h3>
                <p>Create a coupon to start driving footfall to your store.</p>
                <button className="btn btn-primary" onClick={openNew}><PlusIcon /> New Coupon</button>
              </div>
            </div>
          ) : (
            <div className="coupons-list">
              {coupons.map(c => {
                const expired = isExpired(c);
                const discount = couponDiscountDisplay(c);
                return (
                  <div key={c.id} className={`coupon-card ${expired ? 'inactive' : ''}`} onClick={() => setViewCoupon(c)} style={{ cursor: 'pointer' }}>
                    <div className="coupon-discount-tag">
                      <div className="coupon-discount-val">{discount.value}</div>
                      <div className="coupon-discount-type">{discount.type}</div>
                    </div>
                    <div className="coupon-info">
                      <div className="coupon-title">{c.title}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                        <span className="coupon-code">{c.code}</span>
                        <span className={`badge ${expired ? 'badge-gray' : 'badge-green'}`}>
                          {expired ? 'Expired' : expiresIn(c)}
                        </span>
                      </div>
                      <div className="coupon-meta" style={{ marginTop: 4 }}>
                        {new Date(c.validFrom).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        {' → '}
                        {new Date(c.validUntil).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        {c.triggerRadiusM >= 9999 ? ' · Always visible' : ` · Within ${c.triggerRadiusM}m`}
                        {c.maxRedemptions && ` · Max ${c.maxRedemptions}`}
                      </div>
                    </div>
                    <div className="coupon-actions">
                      <button
                        className="btn btn-ghost"
                        style={{ padding: '6px 10px', color: 'var(--danger)' }}
                        onClick={e => { e.stopPropagation(); deactivate(c.id); }}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {viewCoupon && (() => {
        const c = viewCoupon;
        const expired = isExpired(c);
        const discount = couponDiscountDisplay(c);
        const linkedProduct = products.find(p => p.id === c.productId);
        return (
          <div className="modal-overlay" onClick={() => setViewCoupon(null)}>
            <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div>
                  <h2 style={{ margin: 0 }}>{c.title}</h2>
                  {c.description && <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{c.description}</p>}
                </div>
                <span className={`badge ${expired ? 'badge-gray' : 'badge-green'}`} style={{ flexShrink: 0, marginLeft: 12 }}>
                  {expired ? 'Expired' : expiresIn(c)}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  { label: 'Coupon Code', value: <span className="coupon-code">{c.code}</span> },
                  { label: 'Discount', value: discount.detail },
                  { label: 'Valid From', value: new Date(c.validFrom).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) },
                  { label: 'Valid Until', value: new Date(c.validUntil).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) },
                  { label: 'Visibility', value: c.triggerRadiusM >= 9999 ? 'Always visible' : `Within ${c.triggerRadiusM}m of store` },
                  { label: 'Max Redemptions', value: c.maxRedemptions ? c.maxRedemptions.toLocaleString() : 'Unlimited' },
                  { label: 'Times Redeemed', value: (c.redemptions?.length ?? 0).toString() },
                  { label: 'Linked Product', value: linkedProduct ? linkedProduct.name : 'Store-wide' },
                ].map(({ label, value }) => (
                  <div key={label} style={{ background: 'var(--surface-alt, #f8fafc)', borderRadius: 'var(--radius-sm)', padding: '10px 14px' }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>{value}</div>
                  </div>
                ))}
              </div>

              <div className="modal-footer" style={{ marginTop: 20 }}>
                <button
                  className="btn btn-ghost"
                  style={{ color: 'var(--danger)' }}
                  onClick={() => { setViewCoupon(null); deactivate(c.id); }}
                >
                  <TrashIcon /> Deactivate
                </button>
                <button className="btn btn-secondary" onClick={() => setViewCoupon(null)}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <h2>Create Coupon</h2>
            <form onSubmit={save}>
              <div className="form-grid">
                <div className="form-group full" style={{ flexDirection: 'row', gap: 12 }}>
                  <div className="form-group" style={{ flex: 1, minWidth: 0 }}>
                    <label>Discount Type</label>
                    <select name="discountType" value={form.discountType} onChange={handle}>
                      {DISCOUNT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1, minWidth: 0 }}>
                    <label>{discountValueLabel(form.discountType)}</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {form.discountType === 'INTENT_BASED' && (
                        <select name="capType" value={form.capType} onChange={handle} style={{ width: 64, flexShrink: 0 }}>
                          <option value="FLAT">₹</option>
                          <option value="PERCENTAGE">%</option>
                        </select>
                      )}
                      <input name="discountValue" type="number" step="0.01" value={form.discountValue} onChange={handle} required
                        placeholder={
                          form.discountType === 'PERCENTAGE' ? '30' :
                          form.discountType === 'INTENT_BASED' && form.capType === 'PERCENTAGE' ? '30' : '500'
                        } style={{ flex: 1, minWidth: 0 }} />
                    </div>
                  </div>
                </div>
                {form.discountType === 'INTENT_BASED' && (
                  <div className="form-group full">
                    <span className="form-hint">Shoppr's algorithm determines the optimal discount for each shopper based on their behaviour and intent — you only set the cap.</span>
                  </div>
                )}
                <div className="form-group">
                  <label>Max Redemptions</label>
                  <input name="maxRedemptions" type="number" value={form.maxRedemptions} onChange={handle} placeholder="Unlimited" />
                </div>
                <div className="form-group full" style={{ flexDirection: 'row', gap: 12 }}>
                  <div className="form-group" style={{ flex: 1, minWidth: 0 }}>
                    <label>Valid From</label>
                    <input name="validFrom" type={showTime ? 'datetime-local' : 'date'} value={form.validFrom} onChange={handle} required />
                  </div>
                  <div className="form-group" style={{ flex: 1, minWidth: 0 }}>
                    <label>Valid Until</label>
                    <input name="validUntil" type={showTime ? 'datetime-local' : 'date'} value={form.validUntil} onChange={handle} required />
                  </div>
                </div>
                <div className="form-group full">
                  <label>Applies To</label>
                  <select name="scope" value={form.scope} onChange={e => { handle(e); setProductSearch(''); }}>
                    <option value="store">Entire store — all products</option>
                    <option value="products">Select products only</option>
                  </select>
                  {form.scope === 'products' && (
                    <div style={{ marginTop: 8, position: 'relative' }}>
                      {/* Selected chips */}
                      {form.productIds.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                          {form.productIds.map(id => {
                            const p = products.find(x => x.id === id);
                            return (
                              <span key={id} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                background: 'var(--primary-light)', color: 'var(--primary)',
                                border: '1px solid var(--primary)', borderRadius: 20,
                                padding: '3px 10px', fontSize: '0.8rem', fontWeight: 500,
                              }}>
                                {p?.name}
                                <button type="button" onClick={() => setForm(f => ({ ...f, productIds: f.productIds.filter(x => x !== id) }))}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: 'var(--primary)', fontSize: '1rem' }}>×</button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {/* Search input */}
                      <input
                        type="text"
                        placeholder="Search products…"
                        value={productSearch}
                        onChange={e => setProductSearch(e.target.value)}
                        onFocus={() => setShowProductDropdown(true)}
                        onBlur={() => setShowProductDropdown(false)}
                        autoComplete="off"
                      />
                      {/* Dropdown */}
                      {showProductDropdown && (() => {
                        const filtered = products.filter(p =>
                          !form.productIds.includes(p.id) &&
                          p.name.toLowerCase().includes(productSearch.toLowerCase())
                        );
                        return filtered.length > 0 ? (
                          <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                            background: 'var(--surface)', border: '1px solid var(--border)',
                            borderRadius: 'var(--radius-sm)', boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                            maxHeight: 200, overflowY: 'auto',
                          }}>
                            {filtered.map(p => (
                              <button key={p.id} type="button"
                                onMouseDown={e => { e.preventDefault(); setProductSearch(''); setForm(f => ({ ...f, productIds: [...f.productIds, p.id] })); }}
                                style={{
                                  display: 'block', width: '100%', padding: '9px 14px',
                                  border: 'none', background: 'none', cursor: 'pointer',
                                  textAlign: 'left', fontSize: '0.875rem', color: 'var(--text)',
                                  fontFamily: 'inherit',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-alt, #f8fafc)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'none'}
                              >
                                {p.name}
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginLeft: 8 }}>
                                  ₹{parseFloat(p.price).toLocaleString()}
                                </span>
                              </button>
                            ))}
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Advanced settings */}
              <button type="button" onClick={() => setShowAdvanced(v => !v)} style={{
                display: 'flex', alignItems: 'center', gap: 6, marginTop: 16,
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                fontSize: '0.8rem', color: 'var(--text-secondary)', fontFamily: 'inherit',
              }}>
                <span style={{ fontSize: '0.7rem' }}>{showAdvanced ? '▼' : '▶'}</span>
                Advanced settings
              </button>

              {showAdvanced && (
                <div className="form-grid" style={{ marginTop: 12 }}>
                  <div className="form-group full">
                    <label>Title <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(auto-generated if blank)</span></label>
                    <input name="title" value={form.title} onChange={handle} placeholder="e.g. Weekend Flash Sale" />
                  </div>
                  <div className="form-group full">
                    <label>Description</label>
                    <input name="description" value={form.description} onChange={handle} placeholder="e.g. Valid on all footwear" />
                  </div>
                  <div className="form-group">
                    <label>Coupon Code</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input name="code" value={form.code} onChange={handle} required style={{ flex: 1 }} />
                      <button type="button" className="btn btn-secondary" style={{ padding: '8px 12px' }} onClick={() => setForm(f => ({ ...f, code: randomCode() }))}>
                        <RefreshIcon />
                      </button>
                    </div>
                  </div>
                  <div className="form-group full" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="checkbox" id="showTimeCoupons" checked={showTime} onChange={e => toggleTime(e.target.checked)} style={{ width: 'auto', margin: 0 }} />
                    <label htmlFor="showTimeCoupons" style={{ margin: 0, cursor: 'pointer', fontWeight: 400 }}>Set specific times for valid from / until</label>
                  </div>
                </div>
              )}

              {error && <div className="alert alert-error" style={{ marginTop: 12 }}>{error}</div>}
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Creating…' : 'Create Coupon'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
